const MESSAGE_HIDE_DELAY = 3000;
const SUBMIT_DEBOUNCE_DELAY = 600;
const SUBMIT_LOCK_FALLBACK_DELAY = 10000;

/** @type {number | undefined} */
let hideTimer;
const objectSubmitTimes = new WeakMap();
const primitiveSubmitTimes = new Map();

function ensureMessageElement() {
  let element = document.querySelector('.submit-feedback-message');
  if (element instanceof HTMLElement) return element;

  element = document.createElement('div');
  element.className = 'submit-feedback-message';
  element.setAttribute('role', 'status');
  element.setAttribute('aria-live', 'polite');
  element.innerHTML = '<span class="submit-feedback-message__dot" aria-hidden="true"></span><span></span>';
  document.body.appendChild(element);

  if (!document.getElementById('submit-feedback-message-style')) {
    const style = document.createElement('style');
    style.id = 'submit-feedback-message-style';
    style.textContent = `
      .submit-feedback-message {
        position: fixed;
        inset-inline: 50%;
        top: max(18px, env(safe-area-inset-top));
        z-index: 2147483647;
        display: flex;
        align-items: center;
        gap: 10px;
        max-width: min(420px, calc(100vw - 32px));
        padding: 12px 16px;
        border: 1px solid rgba(255, 181, 71, 0.35);
        border-radius: 999px;
        background: rgba(14, 20, 24, 0.94);
        color: #f8f8f2;
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.36);
        font: 600 14px/1.35 var(--font-body--family, system-ui, sans-serif);
        opacity: 0;
        pointer-events: none;
        transform: translateX(-50%) translateY(-12px);
        transition: opacity 180ms ease, transform 180ms ease;
      }

      .submit-feedback-message.is-visible {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }

      .submit-feedback-message[data-type="error"] {
        border-color: rgba(255, 96, 96, 0.5);
      }

      .submit-feedback-message__dot {
        width: 8px;
        height: 8px;
        flex: 0 0 8px;
        border-radius: 999px;
        background: #ffb547;
        box-shadow: 0 0 18px rgba(255, 181, 71, 0.65);
      }

      .submit-feedback-message[data-type="error"] .submit-feedback-message__dot {
        background: #ff6060;
        box-shadow: 0 0 18px rgba(255, 96, 96, 0.55);
      }

      [data-submit-busy="true"] {
        cursor: wait;
      }
    `;
    document.head.appendChild(style);
  }

  return element;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeMessage(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(normalizeMessage).filter(Boolean).join(' ');
  if (typeof value === 'object') {
    return Object.values(value)
      .map(normalizeMessage)
      .filter(Boolean)
      .join(' ');
  }
  return String(value);
}

/**
 * @param {unknown} data
 * @param {string} fallback
 * @returns {string}
 */
export function responseMessage(data, fallback = 'Updated successfully.') {
  if (!data || typeof data !== 'object') return fallback;

  const payload = /** @type {{ message?: unknown; description?: unknown; errors?: unknown; error?: unknown }} */ (data);
  return (
    normalizeMessage(payload.message) ||
    normalizeMessage(payload.description) ||
    normalizeMessage(payload.errors) ||
    normalizeMessage(payload.error) ||
    fallback
  );
}

/**
 * @param {string} message
 * @param {'success' | 'error' | 'info'} [type]
 */
export function showSubmissionMessage(message, type = 'success') {
  if (!message) return;

  const element = ensureMessageElement();
  const text = element.querySelector('span:last-child');
  if (text) text.textContent = message;
  element.dataset.type = type;
  element.classList.add('is-visible');

  if (hideTimer) window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    element.classList.remove('is-visible');
  }, MESSAGE_HIDE_DELAY);
}

/**
 * @param {object | string | number | null | undefined} key
 * @param {number} [delay]
 * @returns {boolean}
 */
export function shouldDebounceSubmit(key, delay = SUBMIT_DEBOUNCE_DELAY) {
  if (key === null || key === undefined) return false;

  const now = performance.now();
  if ((typeof key === 'object' || typeof key === 'function') && key !== null) {
    const lastSubmittedAt = objectSubmitTimes.get(key) || 0;
    if (lastSubmittedAt && now - lastSubmittedAt < delay) return true;
    objectSubmitTimes.set(key, now);
    return false;
  }

  const primitiveKey = String(key);
  const lastSubmittedAt = primitiveSubmitTimes.get(primitiveKey) || 0;
  if (lastSubmittedAt && now - lastSubmittedAt < delay) return true;
  primitiveSubmitTimes.set(primitiveKey, now);
  return false;
}

document.addEventListener('submit-feedback:show', (event) => {
  if (!(event instanceof CustomEvent)) return;

  const type = ['success', 'error', 'info'].includes(event.detail?.type) ? event.detail.type : 'success';
  showSubmissionMessage(normalizeMessage(event.detail?.message), type);
});

/**
 * @param {HTMLElement | null | undefined} element
 * @param {boolean} busy
 */
export function setSubmitBusy(element, busy) {
  if (!element) return;
  if (busy) {
    element.setAttribute('data-submit-busy', 'true');
    element.setAttribute('aria-busy', 'true');
    if ('disabled' in element) {
      /** @type {HTMLButtonElement | HTMLInputElement} */ (element).disabled = true;
    }
    return;
  }

  element.removeAttribute('data-submit-busy');
  element.removeAttribute('aria-busy');
  if ('disabled' in element) {
    /** @type {HTMLButtonElement | HTMLInputElement} */ (element).disabled = false;
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.closest('product-form-component, cart-discount-component')) return;

    if (form.dataset.submitLocked === 'true') {
      event.preventDefault();
      return;
    }

    if (shouldDebounceSubmit(form)) {
      event.preventDefault();
      return;
    }

    if (!form.checkValidity()) return;

    const button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (button instanceof HTMLElement) {
      form.dataset.submitLocked = 'true';
      setSubmitBusy(button, true);

      window.setTimeout(() => {
        if (!document.contains(form)) return;
        form.dataset.submitLocked = 'false';
        setSubmitBusy(button, false);
      }, SUBMIT_LOCK_FALLBACK_DELAY);
    }
  },
  true
);

document.addEventListener('DOMContentLoaded', () => {
  const error = document.querySelector(
    '.contact-form__error, .cc-contact-status--error, .form-status.form-status-list'
  );
  if (error?.textContent?.trim()) {
    showSubmissionMessage(error.textContent.trim(), 'error');
    return;
  }

  const success = document.querySelector(
    '.contact-form__success, .cc-contact-status:not(.cc-contact-status--error), .form-status'
  );
  if (success?.textContent?.trim()) {
    showSubmissionMessage(success.textContent.trim(), 'success');
  }
});
